/**
 * A row of filters, each with how many rows it would leave.
 *
 * The count is the whole value of the pattern. A filter that turns out to
 * match nothing is a wasted click and a moment of doubt about whether the
 * page is broken; showing the number in advance removes both.
 *
 * Rendered as real buttons in a group, not links: they mutate a view rather
 * than navigating, and a middle-click on one should do nothing.
 */

import "./data.css";

export interface Chip {
  id: string;
  label: string;
  /** Omit when the count is unknown — better absent than wrong. */
  count?: number;
}

export interface ChipsProps {
  chips: readonly Chip[];
  /** The selected chip's id, or null for "no filter". */
  active: string | null;
  onSelect: (id: string | null) => void;
  /** Announced as the group's name. */
  label: string;
  /** Label for the chip that clears the filter. Omit to hide it. */
  allLabel?: string;
}

export function Chips({ chips, active, onSelect, label, allLabel }: ChipsProps) {
  return (
    <div className="hardauto-chips" role="group" aria-label={label}>
      {allLabel ? (
        <button
          type="button"
          className="hardauto-chip"
          data-active={active === null ? "true" : undefined}
          aria-pressed={active === null}
          onClick={() => onSelect(null)}
        >
          {allLabel}
        </button>
      ) : null}
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          className="hardauto-chip"
          data-active={active === chip.id ? "true" : undefined}
          aria-pressed={active === chip.id}
          // Clicking the active chip clears it. Without that, a filter row
          // with no "all" chip is a trap: once you pick one you cannot get
          // back to the unfiltered list.
          onClick={() => onSelect(active === chip.id ? null : chip.id)}
        >
          {chip.label}
          {chip.count === undefined ? null : (
            <span className="hardauto-chip__count">{chip.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}
